import { useEffect } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  Link
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCampaign } from '../store/slices/campaignsSlice';
import stations from '../data/stations';

const WA_IMAGE_BASE_URL = process.env.REACT_APP_WA_IMAGE_BASE_URL || '/assets/waimages/';

const mapSourceChannelIds = (items = []) =>
  items.map((item) => (item && typeof item === 'object' && item.id !== undefined ? item.id : item));

const mapStationCodes = (items = []) => items.map((item) => (item && item.code ? item.code : item));

const getSourceChannelLabel = (item) => {
  if (item && typeof item === 'object') {
    return item.name || item.label || item.id || item.value;
  }
  return item;
};

const getWaImageSrc = (path) => {
  if (!path) return '';
  const filename = path.split(/[/\\]/).pop();
  return `${WA_IMAGE_BASE_URL}${filename}`;
};

const CampaignPreviewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = location.state?.campaign;

  useEffect(() => {
    if (!data) {
      navigate('/campaigns/new', { replace: true });
    }
  }, [data, navigate]);

  if (!data) return null;

  const stationName = (station) => {
    if (station && typeof station === 'object') {
      return station.name || station.code || station.alias || station;
    }
    return stations.find((item) => item.code === station)?.name || station;
  };

  const handleConfirm = async () => {
    const payload = {
      ...(data.id ? { id: data.id } : {}),
      name: data.name || 'Untitled Campaign',
      status: 'PENDING_APPROVAL',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      priority: data.priority || '-',
      description: data.description || '',
      origins: mapStationCodes(data.origins || []),
      destinations: mapStationCodes(data.destinations || []),
      sourceChannels: mapSourceChannelIds(data.sourceChannels || []),
      timePeriods: data.timePeriods || [],
      channel: data.channel,
      whatsappTemplateId: data.whatsappTemplateId,
      whatsappText: data.whatsappText,
      whatsappHeaderImageId: data.whatsappHeaderImageId,
      totalImpression: data.totalImpression,
      dailyImpression: data.dailyImpression,
      totalImpressionPerCustomer: data.totalPerCustomer,
      dailyImpressionPerCustomer: data.dailyPerCustomer,
      fileUploadId: data.fileUploadId
    };

    try {
      await dispatch(createCampaign(payload)).unwrap();
      navigate('/campaigns');
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert(error || 'Failed to publish campaign');
    }
  };

  const renderList = (label, items) => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      {items?.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {items.map((item) => (
            <Chip key={item} label={item} size="small" />
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary">-</Typography>
      )}
    </Box>
  );

  const renderStations = (label, codes) =>
    renderList(
      label,
      (codes || []).map((code) => stationName(code))
    );

  const renderSourceChannels = (label, items) =>
    renderList(
      label,
      (items || []).map((item) => getSourceChannelLabel(item))
    );

  const renderPhoneUpload = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        Phone Upload
      </Typography>
      <Stack spacing={0.25}>
        <Typography>{data.phoneFileName || '-'}</Typography>
        {data.uploadTotalRows !== undefined && data.uploadTotalRows !== null && (
          <Typography color="text.secondary">Total Rows: {data.uploadTotalRows}</Typography>
        )}
      </Stack>
    </Box>
  );

  const formatUnlimited = (value) => (value === 0 ? 'Unlimitted' : value || '-');

  const renderValueUnlimited = (label, value) => renderValue(label, formatUnlimited(value));

  const renderValue = (label, value) => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Preview Campaign</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Preview Campaign
          </Typography>
          <Typography color="text.secondary">
            Review the details before publishing.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Basic Campaign Information
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2
            }}
          >
            {renderValue('Campaign Name', data.name)}
            {renderValue('Priority', data.priority)}
            {renderValue('Start Date', data.startDate)}
            {renderValue('End Date', data.endDate)}
            <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
              {renderValue('Description', data.description)}
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Targeting
          </Typography>
          <Stack spacing={1.5}>
            {renderSourceChannels('Source Channel', data.sourceChannels)}
            {renderPhoneUpload()}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
              }}
            >
              {renderStations('Origin Stations', data.origins)}
              {renderStations('Destination Stations', data.destinations)}
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Channel
          </Typography>
          <Stack spacing={1.5}>
            {renderValue('Channel', data.channel)}
            {data.channel === 'whatsapp' && (
              <>
                {renderValue('Whatsapp Template Id', data.whatsappTemplateId)}
                {renderValue('Whatsapp Text', data.whatsappText)}
                {data.whatsappFormat === 'IMAGE' && data.whatsappImagePath && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Whatsapp Header Image
                    </Typography>
                    <Box
                      component="img"
                      src={getWaImageSrc(data.whatsappImagePath)}
                      alt="Whatsapp Header"
                      sx={{ maxWidth: 240, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                      onError={(e) => {
                        // hide broken image
                        // eslint-disable-next-line no-param-reassign
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Frequency Control
          </Typography>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                Campaign Level
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2
                }}
              >
                {renderValueUnlimited('Total Impression', data.totalImpression)}
                {renderValueUnlimited('Daily Impression', data.dailyImpression)}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                Customer Level
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2
                }}
              >
                {renderValueUnlimited('Total Impression per Customer', data.totalPerCustomer)}
                {renderValueUnlimited('Daily Impression per Customer', data.dailyPerCustomer)}
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {data.setDaysAndTimes && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Day and time
            </Typography>
            <Stack spacing={1.5}>
              {(data.timePeriods || []).map((period, idx) => (
                <Box
                  key={`${period.startTime}-${period.endTime}-${idx}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Typography>
                      <strong>Start:</strong> {period.startTime || '-'}
                    </Typography>
                    <Typography>
                      <strong>End:</strong> {period.endTime || '-'}
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Repeating on
                    </Typography>
                    {period.days?.length ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {period.days.map((day) => (
                          <Chip key={day} label={day} size="small" />
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => navigate('/campaigns/new', { state: { campaign: data } })}
        >
          Back to Edit
        </Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Request Approval
        </Button>
      </Stack>
    </Stack>
  );
};

export default CampaignPreviewPage;
